import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import BulletinGallery from '../components/BulletinGallery';
import { getAllAnnouncements } from '../data/announcements';

const CommunityContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #fff;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  border-bottom: 1px solid #e9ecef;

  @media (max-width: 768px) {
    overflow-x: auto;
    justify-content: flex-start;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 15px 30px;
  border: none;
  background: none;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.active ? '#2c5aa0' : '#666'};
  border-bottom: 3px solid ${props => props.active ? '#2c5aa0' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: #2c5aa0;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 16px;
  }
`;

const ProgramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProgramCard = styled.div`
  background: #f8f9fa;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ProgramTitle = styled.h3`
  font-size: 24px;
  color: #2c5aa0;
  margin-bottom: 15px;
  font-weight: 700;
`;

const ProgramDescription = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
`;

const ProgramInfo = styled.div`
  font-size: 14px;
  color: #2c5aa0;
  font-weight: 600;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PhotoCard = styled.div`
  background: #fff;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const PhotoImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 16px;
`;

const PhotoContent = styled.div`
  padding: 20px;
`;

const PhotoTitle = styled.h4`
  font-size: 18px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
`;

const PhotoDate = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`;

const PhotoDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const AnnouncementList = styled.div`
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const AnnouncementItem = styled.div`
  padding: 20px 30px;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fa;
  }
`;

const AnnouncementTitle = styled.h4`
  font-size: 18px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 600;
`;

const AnnouncementMeta = styled.div`
  display: flex;
  gap: 15px;
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`;

const AnnouncementContent = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.5;
`;

const Community: React.FC = () => {
  const [activeTab, setActiveTab] = useState('programs');

  const [programs, setPrograms] = useState<any[]>([]);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
        const response = await fetch(`${backendUrl}/api/programs`);
        const result = await response.json();
        if (result.success) {
          setPrograms(result.programs);
        }
      } catch (error) {
        console.error('Failed to fetch programs:', error);
      }
    };

    fetchPrograms();
  }, []);

  const events = [
    {
      title: '2025년 신년 감사예배',
      date: '2025-01-01',
      description: '새해를 맞아 하나님께 감사드리는 특별한 예배 시간을 가졌습니다.',
      image: '🎊'
    },
    {
      title: '성탄절 특별예배',
      date: '2024-12-25',
      description: '예수님의 탄생을 기념하는 성탄절 특별예배와 축하 행사가 있었습니다.',
      image: '🎄'
    },
    {
      title: '추수감사절 예배',
      date: '2024-11-24',
      description: '한 해 동안 주신 은혜에 감사드리는 추수감사절 예배를 드렸습니다.',
      image: '🍂'
    },
    {
      title: '교회 창립 기념예배',
      date: '2024-10-15',
      description: '교회 창립을 기념하며 하나님의 은혜를 되새기는 시간을 가졌습니다.',
      image: '🏛️'
    },
    {
      title: '여름 성경학교',
      date: '2024-08-10',
      description: '아이들과 함께하는 즐거운 여름 성경학교가 성황리에 마쳤습니다.',
      image: '📚'
    },
    {
      title: '부활절 연합예배',
      date: '2024-03-31',
      description: '예수님의 부활을 기념하는 영광스러운 부활절 연합예배를 드렸습니다.',
      image: '🌅'
    }
  ];

  // Get synchronized announcements from shared data
  const announcements = getAllAnnouncements();

  return (
    <CommunityContainer>
      <HeroSection>
        <HeroContent>
          <Title>나눔터 및 교회소식</Title>
          <Subtitle>교회의 다양한 프로그램과 소식을 함께 나누는 공간입니다</Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ContentContainer>
          <TabContainer>
            <Tab
              active={activeTab === 'programs'}
              onClick={() => setActiveTab('programs')}
            >
              프로그램 안내
            </Tab>
            <Tab
              active={activeTab === 'events'}
              onClick={() => setActiveTab('events')}
            >
              교회 행사 사진
            </Tab>
            <Tab
              active={activeTab === 'announcements'}
              onClick={() => setActiveTab('announcements')}
            >
              공지사항
            </Tab>
            <Tab
              active={activeTab === 'bulletins'}
              onClick={() => setActiveTab('bulletins')}
            >
              주보
            </Tab>
          </TabContainer>

          {activeTab === 'programs' && (
            <ProgramGrid>
              {programs.length > 0 ? (
                programs.map((program, index) => (
                  <ProgramCard key={index}>
                    <ProgramTitle>{program.title}</ProgramTitle>
                    <ProgramDescription>{program.description}</ProgramDescription>
                    <ProgramInfo>
                      📅 {program.date} {program.time}<br />
                      📍 {program.location}
                      {program.fileUrl && (
                        <div style={{ marginTop: '10px' }}>
                          <a
                            href={program.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2c5aa0', textDecoration: 'none', fontWeight: 'bold' }}
                          >
                            📄 프로그램 다운로드
                          </a>
                        </div>
                      )}
                    </ProgramInfo>
                  </ProgramCard>
                ))
              ) : (
                <div style={{ textAlign: 'center', gridColumn: '1/-1', padding: '40px', color: '#666' }}>
                  등록된 프로그램이 없습니다.
                </div>
              )}
            </ProgramGrid>
          )}

          {activeTab === 'events' && (
            <PhotoGrid>
              {events.map((event, index) => (
                <PhotoCard key={index}>
                  <PhotoImage>
                    <span style={{ fontSize: '48px' }}>{event.image}</span>
                  </PhotoImage>
                  <PhotoContent>
                    <PhotoTitle>{event.title}</PhotoTitle>
                    <PhotoDate>{event.date}</PhotoDate>
                    <PhotoDescription>{event.description}</PhotoDescription>
                  </PhotoContent>
                </PhotoCard>
              ))}
            </PhotoGrid>
          )}

          {activeTab === 'announcements' && (
            <AnnouncementList>
              {announcements.map((announcement, index) => (
                <AnnouncementItem key={index}>
                  <AnnouncementTitle>{announcement.title}</AnnouncementTitle>
                  <AnnouncementMeta>
                    <span>📅 {announcement.date}</span>
                    <span>✍️ {announcement.author}</span>
                  </AnnouncementMeta>
                  <AnnouncementContent>{announcement.content}</AnnouncementContent>
                </AnnouncementItem>
              ))}
            </AnnouncementList>
          )}

          {activeTab === 'bulletins' && (
            <BulletinGallery />
          )}
        </ContentContainer>
      </ContentSection>
    </CommunityContainer>
  );
};

export default Community;
